import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  Avatar, 
  Chip, 
  Button, 
  Alert,
  CircularProgress,
  Badge,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  VolumeOff as MuteIcon,
  PushPin as PinIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { tgAccountsAPI } from '../../Api';
import PlumSvg from '../component/PlumSvg';

const TGAccountChats = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [account, setAccount] = useState(null);

  const showAlert = (type, message) => {
    setAlert(<Alert severity={type}>{message}</Alert>);
    setTimeout(() => setAlert(null), 5000);
  };

  const loadAccount = async () => {
    try {
      const res = await tgAccountsAPI.getAccountById(id);
      setAccount(res.data?.data);
    } catch (e) {
      showAlert('error', 'Ошибка загрузки аккаунта');
    }
  };

  const loadChats = async () => {
    setLoading(true);
    try {
      const res = await tgAccountsAPI.getChats(id, 50);
      if (res.data?.success) {
        setChats(res.data.data.chats || []);
      } else {
        showAlert('error', 'Ошибка загрузки чатов');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка загрузки чатов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
    loadChats();
  }, [id]);

  const getChatIcon = (chatType) => {
    switch (chatType) {
      case 'chatTypePrivate':
        return <PersonIcon />;
      case 'chatTypeBasicGroup':
      case 'chatTypeSupergroup':
        return <GroupIcon />;
      case 'chatTypeSecret':
        return <PersonIcon color="secondary" />;
      default:
        return <ChatIcon />;
    }
  };

  const getChatTypeLabel = (chatType) => {
    const types = {
      'chatTypePrivate': 'Личный',
      'chatTypeBasicGroup': 'Группа',
      'chatTypeSupergroup': 'Супергруппа',
      'chatTypeSecret': 'Секретный'
    };
    return types[chatType] || chatType;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (messageDate.getTime() === today.getTime() - 86400000) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: '#fefcfb' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/tg-accounts/list')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlumSvg /> Чаты аккаунта
            </Typography>
            {account && (
              <Typography variant="subtitle2" color="text.secondary">
                {account.phone} • {account.type}
              </Typography>
            )}
          </Box>
          <Tooltip title="Обновить список">
            <IconButton onClick={loadChats} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {alert}

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Chip 
            label={`Всего чатов: ${chats.length}`} 
            color="primary" 
            variant="outlined"
          />
          <Chip 
            label={`Непрочитанных: ${chats.filter(c => c.unread_count > 0).length}`} 
            color="warning" 
            variant="outlined"
          />
          <Chip 
            label={`Закрепленных: ${chats.filter(c => c.is_pinned).length}`} 
            color="info" 
            variant="outlined"
          />
        </Box>

        {/* Chats List */}
        <Paper elevation={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : chats.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Чаты не найдены
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Возможно, аккаунт не авторизован или нет доступных чатов
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {chats.map((chat, index) => (
                <React.Fragment key={chat.id}>
                  <ListItem 
                    button
                    onClick={() => navigate(`/tg-accounts/${id}/chats/${chat.id}/view`)}
                    sx={{ 
                      py: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                      position: 'relative'
                    }}
                  >
                    {/* Chat Avatar */}
                    <ListItemAvatar>
                      <Badge 
                        badgeContent={chat.unread_count > 0 ? chat.unread_count : null} 
                        color="error"
                        max={99}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getChatIcon(chat.type)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>

                    {/* Chat Info */}
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: chat.unread_count > 0 ? 'bold' : 'normal' }}>
                            {truncateText(chat.title, 30)}
                          </Typography>
                          {chat.is_pinned && (
                            <PinIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                          )}
                          {chat.notification_settings.mute_for > 0 && (
                            <MuteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {chat.last_message ? truncateText(chat.last_message.text, 40) : 'Нет сообщений'}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip 
                              label={getChatTypeLabel(chat.type)} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                            {chat.last_message && (
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(chat.last_message.date)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < chats.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>

        {/* Actions */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/tg-accounts/list')}
            sx={{ mr: 2 }}
          >
            Назад к списку
          </Button>
          <Button 
            variant="contained" 
            onClick={loadChats}
            disabled={loading}
            sx={{ backgroundColor: '#683d62' }}
          >
            Обновить чаты
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default TGAccountChats;
